import React, { createContext, useContext, useEffect, useState } from 'react';
import { Language, translations, TranslationNamespaces } from '../translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, namespace?: string) => string;
  tNs: (namespace: keyof TranslationNamespaces) => any;
  dir: 'ltr' | 'rtl';
  isLoading: boolean;
  error: string | null;
  availableLanguages: Language[];
  isLanguageComplete: (lang: Language) => boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableLanguages: Language[] = ['en', 'fr'];
  const isLanguageComplete = (lang: Language): boolean => {
    // For now, only EN and FR are complete
    return lang === 'en' || lang === 'fr';
  };

  // Enhanced translation function with namespace support
  const t = (key: string, namespace?: string): string => {
    try {
      const currentTranslations = translations[language];
      
      if (namespace) {
        const nsTranslations = currentTranslations[namespace as keyof TranslationNamespaces];
        return getNestedValue(nsTranslations, key) || key;
      }
      
      // Try to find the key in any namespace
      for (const ns of Object.keys(currentTranslations)) {
        const nsTranslations = currentTranslations[ns as keyof TranslationNamespaces];
        const value = getNestedValue(nsTranslations, key);
        if (value) return value;
      }
      
      return key; // Fallback to key if not found
    } catch (err) {
      console.warn(`Translation error for key "${key}":`, err);
      return key;
    }
  };

  // Namespace shortcut function
  const tNs = (namespace: keyof TranslationNamespaces) => {
    return translations[language][namespace];
  };

  // Helper function to get nested values from translation objects
  const getNestedValue = (obj: any, path: string): string | null => {
    try {
      return path.split('.').reduce((current, key) => current?.[key], obj) || null;
    } catch {
      return null;
    }
  };

  // Load language from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsHydrated(true);
      return;
    }
    
    try {
      const savedLanguage = localStorage.getItem('voynich-language') as Language;
      if (savedLanguage && availableLanguages.includes(savedLanguage)) {
        setLanguageState(savedLanguage);
        // Set initial direction
        document.documentElement.dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = savedLanguage;
      }
    } catch (err) {
      console.warn('Error loading saved language:', err);
      setError('Failed to load saved language preferences');
    } finally {
      setIsHydrated(true);
    }
  }, []);

  // Update localStorage and document direction when language changes
  const setLanguage = (lang: Language) => {
    if (!availableLanguages.includes(lang) && lang !== 'ar') {
      console.warn(`Language "${lang}" is not available`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (typeof window !== 'undefined') {
        // Add class to prevent transitions during language switch
        document.documentElement.classList.add('switching-language');
        
        localStorage.setItem('voynich-language', lang);
        
        // Set document direction for Arabic
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
        
        // Force a reflow to ensure styles are applied
        void document.documentElement.offsetHeight;
        
        // Remove the class after a frame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.documentElement.classList.remove('switching-language');
            setIsLoading(false);
          });
        });
      } else {
        setIsLoading(false);
      }
      
      setLanguageState(lang);
    } catch (err) {
      console.error('Error changing language:', err);
      setError('Failed to change language');
      setIsLoading(false);
    }
  };

  // Prevent flash of incorrect styles during SSR
  if (!isHydrated) {
    return null;
  }

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    tNs,
    dir: language === 'ar' ? 'rtl' : 'ltr',
    isLoading,
    error,
    availableLanguages,
    isLanguageComplete,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};